import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Search, Filter, Bookmark, BookmarkCheck, ExternalLink, TrendingUp, Users, Target, CheckCircle2, Database, Zap, Lock, Menu, X, Home, Trophy, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRealCreators } from '@/hooks/useRealCreators';
import SEO, { PAGE_SEO } from '@/components/SEO';
import analytics from '@/utils/analytics';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Access control thresholds for creators
// Logged-in users get FULL access (no paywall until payment integration)
const CREATOR_ACCESS = {
  public: { visible: 6, locked: 3, maxRender: 9 },       // Not logged in: 6 visible, 3 locked, stop at 9
  loggedIn: { visible: 100, locked: 0, maxRender: 100 }, // Logged in: FULL access (no paywall)
  premium: { visible: 100, locked: 0, maxRender: 100 }   // Premium: all visible
};

export const BrandDiscovery = () => {
  const navigate = useNavigate();
  // Use the shared hook for fetching real YouTube creators
  const {
    creators,
    loading,
    error,
    apiNote,
    regionFilterActive,
    lastUpdatedFormatted,
    niches,
    nichesLoading,
    selectedNiche,
    selectedRegion,
    setSelectedNiche,
    setSelectedRegion,
    refetch
  } = useRealCreators({
    minSubscribers: 10000,
    limit: 50,
    sortBySubscribers: true,
    autoFetch: true
  });

  // Country/Region options
  const regions = [
    { code: '', name: 'Global' },
    { code: 'IN', name: 'India' },
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'SE', name: 'Sweden' }
  ];

  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [brandEmail, setBrandEmail] = useState('');
  const [showEmailPrompt, setShowEmailPrompt] = useState(true);
  const [whatsNewCount, setWhatsNewCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data && response.data.user_id) {
          setIsLoggedIn(true);
          setIsPremium(response.data.is_premium || false);
        }
      } catch (error) {
        setIsLoggedIn(false);
        setIsPremium(false);
      }
    };
    checkAuth();
  }, []);

  // Compute access thresholds based on auth state (deterministic, no flicker)
  const accessLevel = isPremium ? 'premium' : (isLoggedIn ? 'loggedIn' : 'public');
  const { visible: visibleCount, locked: lockedCount, maxRender } = CREATOR_ACCESS[accessLevel];

  // Fetch "What's New" count for navbar
  useEffect(() => {
    const fetchWhatsNewCount = async () => {
      try {
        const res = await axios.get(`${API}/data/whats-new-count`);
        setWhatsNewCount(res.data?.count || 0);
      } catch (error) {
        // Silently fail - not critical
      }
    };
    fetchWhatsNewCount();
  }, []);

  useEffect(() => {
    if (brandEmail) {
      loadBookmarks();
    }
  }, [brandEmail]);

  const loadBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/brands/bookmarks/${encodeURIComponent(brandEmail)}`);
      const ids = new Set(response.data.bookmarked_creators.map(c => c.creator_id));
      setBookmarkedIds(ids);
    } catch (error) {
      console.error('Failed to load bookmarks:', error);
    }
  };

  const toggleBookmark = async (creatorId) => {
    if (!brandEmail) {
      toast.error('Please enter your email first');
      setShowEmailPrompt(true);
      return;
    }

    if (bookmarkedIds.has(creatorId)) {
      try {
        await axios.delete(`${API}/brands/bookmarks/${creatorId}_bookmark`);
        setBookmarkedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(creatorId);
          return newSet;
        });
        toast.success('Bookmark removed');
      } catch (error) {
        console.error('Failed to remove bookmark:', error);
      }
    } else {
      try {
        await axios.post(`${API}/brands/bookmarks?creator_id=${creatorId}&brand_email=${encodeURIComponent(brandEmail)}`);
        setBookmarkedIds(prev => new Set([...prev, creatorId]));
        toast.success('Creator bookmarked!');
      } catch (error) {
        console.error('Failed to bookmark:', error);
        toast.error('Failed to bookmark creator');
      }
    }
  };

  const handleEmailSubmit = () => {
    if (brandEmail) {
      setShowEmailPrompt(false);
      loadBookmarks();
      toast.success('Email saved! You can now bookmark creators.');
    }
  };

  const handleNicheFilter = (value) => {
    setSelectedNiche(value === 'all' ? 'All Niches' : value);
  };

  const handleReset = () => {
    setSelectedNiche('All Niches');
    setSelectedRegion('');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Meta Tags */}
      <SEO {...PAGE_SEO.brandsDiscover} />
      
      {/* Navigation - Responsive */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
            <span className="text-lg md:text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm">
                <Database className="w-4 h-4 mr-1.5" />
                Discover Brands
              </Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm" className="text-indigo-600 bg-indigo-50">
                <Users className="w-4 h-4 mr-1.5" />
                Discover Creators
              </Button>
            </Link>
            {whatsNewCount > 0 && (
              <Link to="/new-this-week">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-emerald-700 hover:bg-emerald-50">
                  <Zap className="w-4 h-4 mr-1" />
                  <Badge className="ml-1 bg-emerald-100 text-emerald-700 text-xs font-medium px-1.5 py-0 h-5">
                    {whatsNewCount > 99 ? '99+' : whatsNewCount}
                  </Badge>
                </Button>
              </Link>
            )}
            <Link to="/leaderboards">
              <Button variant="ghost" size="sm">
                <Trophy className="w-4 h-4 mr-1.5" />
                Leaderboards
              </Button>
            </Link>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                Analyze Sponsorships
              </Button>
            </Link>
          </div>
          
          {/* Mobile Navigation Controls */}
          <div className="flex md:hidden items-center gap-2">
            <Link to="/">
              <Button variant="ghost" size="sm" className="h-8 px-2">
                <Home className="w-4 h-4" />
              </Button>
            </Link>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-slate-700" />
              ) : (
                <Menu className="w-5 h-5 text-slate-700" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              <Link 
                to="/" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Home className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Home</span>
              </Link>
              <Link 
                to="/explore-brands" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Database className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Discover Brands</span>
              </Link>
              <Link 
                to="/brands/discover" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-indigo-50 text-indigo-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Users className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Discover Creators</span>
                <Badge className="ml-auto bg-indigo-100 text-indigo-700 text-xs">Current</Badge>
              </Link>
              <Link 
                to="/leaderboards" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Trophy className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Leaderboards</span>
              </Link>
              {whatsNewCount > 0 && (
                <Link 
                  to="/new-this-week" 
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Zap className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">What's New</span>
                  <Badge className="ml-auto bg-emerald-100 text-emerald-700 text-xs">
                    {whatsNewCount > 99 ? '99+' : whatsNewCount}
                  </Badge>
                </Link>
              )}
              <Link 
                to="/brands/login" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Building2 className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">For Brands</span>
              </Link>
            </div>
          </div>
        )}
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}} data-testid="discovery-heading">
            Discover YouTube Creators
          </h1>
          <p className="text-lg text-slate-600 mb-2">Find creators perfect for your brand sponsorship campaigns</p>
          {whatsNewCount > 0 && (
            <p className="text-sm text-emerald-600">
              <Zap className="w-4 h-4 inline mr-1" />
              New creators added this week
            </p>
          )}
        </div>

        {/* Email Prompt */}
        {showEmailPrompt && (
          <Card className="p-6 mb-8 bg-gradient-to-br from-indigo-50 to-slate-50">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Save Your Favorites</h3>
                <p className="text-slate-600 mb-4">Enter your email to bookmark creators and build your shortlist</p>
                <div className="flex gap-3">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={brandEmail}
                    onChange={(e) => setBrandEmail(e.target.value)}
                    className="max-w-sm"
                    data-testid="brand-email-input"
                  />
                  <Button onClick={handleEmailSubmit} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-email-btn">
                    Save Email
                  </Button>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowEmailPrompt(false)}>
                Skip
              </Button>
            </div>
          </Card>
        )}

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <Filter className="w-5 h-5 text-slate-600" />
                <h2 className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Filters</h2>
              </div>

              <div className="space-y-6">
                {/* Niche Filter - Dynamic from API */}
                <div>
                  <Label className="mb-2 block">Niche</Label>
                  {nichesLoading ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm py-2">
                      <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                      Loading niches...
                    </div>
                  ) : (
                    <Select 
                      value={selectedNiche === 'All Niches' ? 'all' : selectedNiche} 
                      onValueChange={handleNicheFilter}
                    >
                      <SelectTrigger className="w-full" data-testid="niche-filter">
                        <SelectValue placeholder="All Niches" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Niches</SelectItem>
                        {niches.map(niche => (
                          <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Info text */}
                <p className="text-xs text-slate-500">
                  Showing creators with 10K+ subscribers
                </p>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <Button onClick={() => refetch()} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="search-btn">
                    <Search className="w-4 h-4 mr-2" />
                    Refresh Results
                  </Button>
                  <Button onClick={handleReset} variant="outline" className="w-full" data-testid="reset-btn">
                    Reset Filters
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {/* Data Status Banner - Clean user-friendly messaging */}
            {lastUpdatedFormatted && (
              <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-700">Showing latest analyzed creators</p>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {lastUpdatedFormatted}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-6">
              <p className="text-slate-600">
                {loading ? 'Finding creators for you...' : error ? 'Unable to load creators' : 
                  `${creators.length} creators found${selectedNiche !== 'All Niches' ? ` in ${selectedNiche}` : ''}${selectedRegion && regionFilterActive ? ` (${regions.find(r => r.code === selectedRegion)?.name || selectedRegion})` : ''}`}
              </p>
              {brandEmail && bookmarkedIds.size > 0 && (
                <Link to={`/brands/bookmarks/${encodeURIComponent(brandEmail)}`}>
                  <Button variant="outline" data-testid="view-bookmarks-btn">
                    <BookmarkCheck className="w-4 h-4 mr-2" />
                    View Bookmarks ({bookmarkedIds.size})
                  </Button>
                </Link>
              )}
            </div>

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600">Searching real YouTube creators...</p>
                <p className="text-sm text-slate-500 mt-2">Fetching live data from YouTube API</p>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <Card className="p-12 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-slate-600 mb-4">{error}</p>
                <Button onClick={() => refetch()} variant="outline">
                  Try Again
                </Button>
              </Card>
            )}

            {/* Empty State */}
            {!loading && !error && creators.length === 0 && (
              <Card className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-600 mb-2">No creators found matching your criteria</p>
                <p className="text-sm text-slate-500 mb-4">Try selecting a different niche or resetting filters</p>
                <Button onClick={handleReset} variant="outline">
                  Reset Filters
                </Button>
              </Card>
            )}

            {/* Creator Cards - Real YouTube Data */}
            {!loading && !error && creators.length > 0 && (
              <>
                {/* Subtle value message */}
                <p className="text-sm text-slate-500 text-center mb-6">
                  Discover sponsorship patterns across creators
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Slice creators BEFORE render for performance - backend returns full dataset */}
                  {creators.slice(0, maxRender).map((creator, index) => {
                    // Determine card state: visible, locked, or hidden
                    const isLocked = index >= visibleCount;
                    // CTA ONLY at index 6 (first locked item for public users)
                    const showSignInCTA = !isLoggedIn && index === 6;
                    // Upgrade CTA ONLY at first locked item for logged-in free users
                    const showUpgradeCTA = isLoggedIn && !isPremium && index === CREATOR_ACCESS.loggedIn.visible;
                    
                    return (
                      <Card 
                        key={creator.channel_id || index} 
                        className={`p-6 transition-shadow relative ${isLocked ? 'cursor-not-allowed' : 'hover:shadow-lg'}`}
                        style={isLocked ? { 
                          opacity: 0.5,
                          pointerEvents: 'none'
                        } : {}}
                        tabIndex={isLocked ? -1 : 0}
                        data-testid={`creator-card-${index}`}
                      >
                        {/* Sign-in CTA - ONLY at index 6 (lighter treatment for creators) */}
                        {showSignInCTA && (
                          <div 
                            className="absolute inset-0 bg-white/70 rounded-lg flex flex-col items-center justify-center z-10"
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Lock className="w-6 h-6 text-indigo-600 mb-3" />
                            <p className="text-sm text-slate-700 text-center px-4 mb-3 font-medium">
                              Sign in to explore more creators and sponsorships
                            </p>
                            <Button 
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/login?redirect=/brands/discover');
                              }}
                            >
                              Sign in
                            </Button>
                          </div>
                        )}
                        
                        {/* Upgrade CTA - ONLY at first locked item for logged-in users */}
                        {showUpgradeCTA && (
                          <div 
                            className="absolute inset-0 bg-white/70 rounded-lg flex flex-col items-center justify-center z-10"
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Lock className="w-6 h-6 text-indigo-600 mb-3" />
                            <p className="text-sm text-slate-700 text-center px-4 mb-3 font-medium">
                              Unlock full creator database
                            </p>
                            <Button 
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/dashboard');
                              }}
                            >
                              Upgrade
                            </Button>
                          </div>
                        )}
                      
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start gap-3 flex-1">
                            {/* Creator Thumbnail */}
                            {creator.thumbnail && (
                              <img 
                                src={creator.thumbnail} 
                                alt={creator.channel_name}
                                className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-xl font-semibold text-slate-900 truncate" style={{fontFamily: 'Manrope'}}>
                                  {creator.channel_name}
                                </h3>
                                {/* Dynamic badge based on data source */}
                                {creator.is_cached || apiNote ? (
                                  <div 
                                    className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full cursor-help" 
                                    title={lastUpdatedFormatted ? lastUpdatedFormatted : 'Previously analyzed creator'}
                                  >
                                    <Database className="w-3 h-3" />
                                    <span className="text-xs font-medium">{lastUpdatedFormatted ? lastUpdatedFormatted.replace('Updated ', '') : 'Analyzed'}</span>
                                  </div>
                                ) : (
                                  <div 
                                    className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full cursor-help" 
                                    title="Latest creator data"
                                  >
                                    <CheckCircle2 className="w-3 h-3" />
                                    <span className="text-xs font-medium">Latest</span>
                                  </div>
                                )}
                              </div>
                              {creator.youtube_link && !isLocked && (
                                <a 
                                  href={creator.youtube_link} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1 mt-1"
                                >
                                  Visit Channel <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                          {!isLocked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleBookmark(creator.channel_id)}
                              className={bookmarkedIds.has(creator.channel_id) ? 'text-indigo-600' : 'text-slate-400'}
                              data-testid={`bookmark-btn-${index}`}
                            >
                              {bookmarkedIds.has(creator.channel_id) ? (
                                <BookmarkCheck className="w-5 h-5" />
                              ) : (
                                <Bookmark className="w-5 h-5" />
                              )}
                            </Button>
                          )}
                        </div>

                        {/* Stats Grid - Keep visible even for locked (lighter treatment) */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="w-4 h-4 text-slate-500" />
                              <p className="text-xs uppercase tracking-wider text-slate-500">Subscribers</p>
                            </div>
                            <p className="text-lg font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                              {creator.subscriber_count?.toLocaleString()}
                            </p>
                          </div>
                        </div>

                        {/* Niche Badge */}
                        <div className="mb-4">
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
                            {creator.niche || selectedNiche}
                          </span>
                        </div>

                        {/* View Channel Button */}
                        {creator.youtube_link && !isLocked && (
                          <a 
                            href={creator.youtube_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block"
                            onClick={() => {
                              analytics.creatorClick(creator.channel_name, creator.channel_id, 'brands_discover');
                              analytics.externalLinkClick(creator.youtube_link, 'youtube_channel', 'brands_discover');
                            }}
                          >
                            <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`view-profile-btn-${index}`}>
                              View YouTube Channel
                              <ExternalLink className="w-4 h-4 ml-2" />
                            </Button>
                          </a>
                        )}
                        
                        {/* Locked state button - completely non-interactive */}
                        {isLocked && (
                          <Button 
                            className="w-full bg-slate-200 text-slate-500 cursor-not-allowed"
                            disabled
                            tabIndex={-1}
                          >
                            Sign in to view
                          </Button>
                        )}
                      </Card>
                    );
                  })}
                </div>
                
                {/* Sign in prompt for non-logged in users */}
                {!isLoggedIn && creators.length >= maxRender && (
                  <div className="text-center mt-8">
                    <p className="text-slate-600 mb-4">Sign in to discover more creators</p>
                    <Button 
                      onClick={() => navigate('/login?redirect=/brands/discover')}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
                    >
                      Sign in to see all creators
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* SEO Content Section - For Search Engines */}
      <section className="bg-white border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* SEO-friendly H2 and description - visible on page load */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
              Find YouTube Creators for Brand Sponsorships
            </h2>
            <p className="text-slate-600 max-w-3xl leading-relaxed">
              Sponsiq's creator discovery dashboard helps brands and marketers find YouTube influencers actively accepting sponsorships. 
              Our database tracks thousands of creators across tech, gaming, finance, lifestyle, and education niches. 
              Filter by subscriber count, niche, and region to find the perfect influencer for your brand partnership campaign.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Why Use Sponsiq */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Why Brands Choose Sponsiq
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Real YouTube data, not estimated metrics</li>
                <li>• Filter creators by niche and audience size</li>
                <li>• Track brands actively sponsoring creators</li>
                <li>• Build your shortlist with bookmarks</li>
              </ul>
            </div>
            
            {/* Explore More */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Explore the Marketplace
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/explore-brands" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Browse Brands Sponsoring Creators
                  </Link>
                </li>
                <li>
                  <Link to="/leaderboards" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → View Top Creator Leaderboards
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Analyze Your YouTube Channel
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Popular Niches */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Popular Sponsorship Niches
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Find YouTube sponsors in Technology, Gaming, Finance, Education, Fitness, and Lifestyle. 
                Our influencer sponsorship marketplace connects brands with creators who have engaged audiences ready for partnerships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm">
          <p>© {new Date().getFullYear()} Sponsiq. The influencer sponsorship marketplace.</p>
        </div>
      </footer>
    </div>
  );
};

export default BrandDiscovery;
